export const API_BASE: string =
  (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_BASE_URL ||
  "http://localhost:8000";

// The book every request is scoped to (Paper · Live · …). Set by the book
// selector; appended to every API call so the backend returns that book's
// ledger and uses that book's account.
let activeBook = "paper";
export function setActiveBook(book: string) { activeBook = book; }
export function getActiveBook() { return activeBook; }

// The Ledger page's PAGE-LOCAL book selection (deliberately independent of
// the sidebar's active book). Published here so the frame's scope chip can
// tell the truth about what that page is actually showing — the chip used to
// claim the sidebar's book while the page defaulted to "all" (Kaizen 2026-08
// D4/F5). Same module-level pattern as activeBook + a DOM event Layout
// listens for.
let ledgerPageBook = "all";
export function setLedgerPageBook(book: string) {
  ledgerPageBook = book;
  window.dispatchEvent(new CustomEvent("stonky:ledger-book"));
}
export function getLedgerPageBook() { return ledgerPageBook; }

// Same idea for Holdings, which rolls up ACROSS books: the frame chip claimed
// the sidebar's book over an all-books, paper-inclusive page (2026-08-19,
// found auditing the paper/real split). The page publishes what it is really
// showing — a book key, or "real"/"paper" for its all-books money scope.
let holdingsPageScope = "real";
export function setHoldingsPageScope(scope: string) {
  holdingsPageScope = scope;
  window.dispatchEvent(new CustomEvent("stonky:holdings-scope"));
}
export function getHoldingsPageScope() { return holdingsPageScope; }

// One-shot mailbox for "jump to Graph Explorer with these nodes highlighted"
// (Trade Story's "View full graph" button) — there's no router in this SPA,
// so a cross-view handoff can't ride the URL or props (ALL_VIEWS in Layout.tsx
// is deliberately uniform, every view just takes `manifest`). A module-level
// value + a DOM event Layout already listens for is the smallest thing that
// works: set the payload, fire the event, the target view claims it once.
let pendingGraphFocus: string[] | null = null;
export function focusGraph(ids: string[]) {
  pendingGraphFocus = ids;
  window.dispatchEvent(new CustomEvent("stonky:navigate-graph"));
}
export function takeGraphFocus(): string[] | null {
  const ids = pendingGraphFocus;
  pendingGraphFocus = null;
  return ids;
}

// Whole → part: The Firm (the landing view) hands off to a book-scoped page —
// clicking a book opens Command on THAT book. Layout owns both halves of that
// (pickBook for the book, setActive for the view), so this is only the message.
// Same module-mailbox + DOM event pattern as focusGraph above; there's no
// router, and ALL_VIEWS is deliberately uniform (every view just takes
// `manifest`), so a prop can't carry it.
let pendingNav: { view: string; book?: string } | null = null;
export function openView(view: string, book?: string) {
  pendingNav = { view, book };
  window.dispatchEvent(new CustomEvent("stonky:open-view"));
}
export function takeNav(): { view: string; book?: string } | null {
  const n = pendingNav;
  pendingNav = null;
  return n;
}

function withBook(path: string): string {
  // A caller that already put its own `book=` in the path (only Performance's
  // /summary?book=<page-local-selector> does this — its filter is deliberately
  // independent of the sidebar's active book) gets left alone. Otherwise this
  // used to blindly append a SECOND book param, and FastAPI resolves duplicate
  // query keys to the last one — silently overriding the page's own filter
  // with whatever the sidebar happened to have selected, unnoticed whenever
  // they differed. Found 2026-08-04 verifying the Performance page's own
  // clarity work: switching its book filter without also touching the
  // sidebar did nothing.
  if (/[?&]book=/.test(path)) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}book=${encodeURIComponent(activeBook)}`;
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${withBook(path)}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail ?? detail);
    } catch {
      /* ignore */
    }
    throw new Error(`${res.status}: ${detail}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(p: string) => req<T>(p),
  post: <T>(p: string, body?: unknown) =>
    req<T>(p, { method: "POST", body: body !== undefined ? JSON.stringify(body) : undefined }),
  put: <T>(p: string, body?: unknown) =>
    req<T>(p, { method: "PUT", body: body !== undefined ? JSON.stringify(body) : undefined }),
  del: <T>(p: string) => req<T>(p, { method: "DELETE" }),
};

// ── Types ─────────────────────────────────────────────────────────────────────

export type Me = {
  id: string;
  username: string;
  must_change_password: boolean;
  tenant_id: string | null;
  tenant_name: string | null;
  halted: boolean | null;
  default_book: string | null;
};

export type Manifest = {
  tenant: string;
  halted: boolean;
  frontend: { theme: string; refresh_seconds: number; panels: { id: string; title: string; visible: boolean }[] };
  schedule: Record<string, string>;
  desk: { juniors: string[]; universe: string[] };
  agents: Record<string, { title: string; model?: string; archetype?: string }>;
};

export type Directive = {
  id: string; seq: number; text: string; status: string; trading_day: string; created_at: string;
};

export type BoardSession = {
  id: string; kind: string; status: string; summary: string | null;
  perspectives: Record<string, { model: string; text: string }> | null;
  deliberation: Record<string, unknown> | null;
  error: string | null; started_at: string; completed_at: string | null;
  directives: Directive[];
};

export type TriggerEvent = {
  id: string; stream: string; severity: string; title: string;
  detail: unknown; board_session_id: string | null; created_at: string;
};

export type Constitution = {
  id: string; version: number; content: string; content_hash: string;
  status: string; ratified_at: string | null; created_at: string;
};

export type Proposal = {
  id: string; junior: string; symbol: string; side: string; qty: number;
  entry_type: string; limit_price: number | null;
  exit_target: number | null; exit_stop: number | null; exit_time: string | null;
  rationale: string; cited_directives: number[] | null; status: string;
  peer_feedback: Record<string, { stance: string; comment: string }> | null;
  risk_report: { allowed: boolean; checks: { name: string; ok: boolean; detail: string }[] } | null;
  trading_day: string; created_at: string;
};

export type Ruling = { id: string; proposal_id: string; decision: string; reasoning: string; created_at: string };

export type DeskPlan = {
  id: string; trading_day: string;
  plan: {
    narrative?: string;
    slices?: Record<string, { budget_usd: number; focus: string }>;
    // The hand the plan was dealt — deployable is tonight's utilization denominator.
    hand?: { equity: number; buying_power: number; deployable: number; per_trade_cap: number; risk_per_trade_usd: number };
  };
  created_at: string;
};

// The other half of a round trip, FIFO-matched by the same code the
// Performance page uses. "closes" = this order shut a position that an
// earlier order opened; "closed_by" = this order's position was later shut.
export type OrderLink = {
  role: "closes" | "closed_by";
  counterpart_order_id: string;
  symbol: string; qty: number; pnl: number; side: string;
  trade_key: string | null;
  entry_time: string | null; exit_time: string | null;
};
export type Order = {
  id: string; symbol: string; side: string; qty: number; order_type: string;
  limit_price: number | null; purpose: string; status: string;
  filled_qty: number; filled_avg_price: number | null;
  broker_order_id: string | null; submitted_at: string;
  error_reason: string | null;
  links: OrderLink[];
};

export type Account = {
  cash: number; equity: number; buying_power: number; portfolio_value: number; status: string; env: string;
  // Combined equity across every venue actually funding this book (Alpaca +
  // Kraken/OANDA when they're genuinely separate accounts, not simulated
  // through Alpaca paper) — see api/trading.py's account() endpoint.
  total_equity: number; venues: Record<string, number>;
  // Venues whose read FAILED while building total_equity ("kraken: …"). A
  // non-empty list means total_equity is a lower bound, not the firm — the
  // UI must say so instead of letting a venue silently vanish (Kaizen D2/F6).
  venue_errors?: string[];
};

export type Position = {
  symbol: string; qty: number; avg_entry_price: number; market_value: number; unrealized_pl: number;
};

export type RiskState = {
  trading_day: string; start_equity: number | null;
  // Venue-inclusive session-open snapshot (Alpaca + Kraken + OANDA). Null
  // until the scheduler has marked it — Day P&L falls back to Alpaca-only
  // and says so (Kaizen D1/F1).
  start_total_equity: number | null;
  realized_pnl: number;
  day_trades_used: number; losing_streak: number; no_new_entries: boolean;
  flattened: boolean; halted: boolean; tenant_halted: boolean; halt_reason: string | null;
  entries_blocked: boolean; entries_blocked_reason: string | null;
  floors: Record<string, number>; soft: Record<string, number>;
};

export type Incident = {
  id: string; severity: string; title: string; detail: string | null;
  status: string; blocks_open: boolean; created_at: string;
};

export type Book = {
  book_key: string;
  label: string;
  enabled: boolean;
  always_on: boolean;
  account_mode: string;
  account_env: string | null;
};

export type ActivityEvent = {
  id: number; actor: string; kind: string; payload: Record<string, unknown> | null; ts: string;
};

export type PostMortem = {
  id: string; trading_day: string; content: string; metrics: Record<string, unknown> | null; created_at: string;
};

export type Connection = {
  kind: string; health: string; health_msg: string | null; health_checked_at: string | null;
};

// GET /api/connections/status — every external dependency the firm needs,
// grouped, each with a traffic light from the SAME probe the nightly audit
// runs. Secrets are write-only: `fingerprint` is a last-four, never a key.
export type ConnField = { key: string; label: string; secret: boolean; placeholder: string };

export type ConnStatus = {
  key: string;
  title: string;
  gates: string;            // what STOPS when this goes red
  state: string;            // ok | expiring | unverified | unconfigured | stale | expired
  light: "green" | "amber" | "red";
  detail: string;
  critical: boolean;
  source: "vault" | "env" | "missing" | "keyless";
  fingerprint: string;
  pasteable: boolean;
  vault_kind: string;
  env_vars: string[];
  last_ok_days_ago: number | null;
  expires_on: string | null;
  fields: ConnField[];
};

export type ConnGroup = {
  key: string; title: string; blurb: string; items: ConnStatus[];
};

export type ConnectionsStatus = {
  checked_at: string;
  groups: ConnGroup[];
  summary: { red: number; amber: number; green: number };
};

export type FirmConfig = { version: number; config: Record<string, unknown> };

// GET /api/firm/overview — capital under management, whole → part.
// REAL and PAPER are two separate trees and are never added together: paper is
// a scoreboard, not an asset (FINANCIAL_ENGINE_PLAN §1.3).
export type FirmAccount = {
  key: string; label: string; institution: string;
  kind: string; tax_wrapper: string; money_type: "real" | "paper";
  // null ONLY when the venue read failed — the account still appears, carrying
  // its error, instead of silently shrinking the total (Kaizen D2/F6).
  total: number | null; cash: number | null; deployed: number | null;
  status: string; as_of: string; error?: string | null;
};
export type FirmSleeve = {
  name: string; budget: number; deployed: number; headroom: number;
  unrealized_pnl: number; position_count: number;
};
export type FirmBook = {
  book: string; label: string; money_type: "real" | "paper";
  equity: number; day_pnl: number | null; day_pnl_base: number | null;
  deployed: number; headroom: number; sleeves: FirmSleeve[];
};
export type MoneyTree = {
  money_type: "real" | "paper";
  total: number;
  // An account read failed: the total is a LOWER BOUND and must be labelled so.
  partial: boolean;
  day_change: number | null; day_change_pct: number | null;
  cash: number; deployed: number;
  accounts: FirmAccount[]; books: FirmBook[];
  buffers: Record<string, number>;
};
export type FirmOverview = {
  as_of: string; real: MoneyTree; paper: MoneyTree;
  venue_health: { venue: string; status: string; last_error?: string | null }[];
};

// ── The Bull Sheet After Hours ──────────────────────────────────────────────
export type PodcastAudit = { ok: boolean; checks: { name: string; ok: boolean; detail: string }[]; words: number; est_minutes: number };
export type PodcastEpisode = {
  id: string; show_day: string; number: number; status: string; title: string;
  audit: PodcastAudit | null; audio_url: string | null; duration_s: number | null;
  created_at: string | null; guest: string | null; lead_headline: string | null;
  description?: string; artist?: string; album?: string;
  share_url?: string | null; stream_url?: string | null; shared_at?: string | null;
  /** set on a private preview (status "preview"): its name in the preview store */
  preview?: string;
  barry?: { night: string; bet: { symbol: string; name?: string; entry: number; target: number; stop: number;
    horizon_days: number; barry_pct?: number | null; thesis?: string;
    odds?: { payout?: number; house_touch_pct?: number; breakeven_pct?: number } } | null } | null;
};
export type PodcastEpisodePage = { total: number; offset: number; limit: number; items: PodcastEpisode[] };
export type ShowPrediction = {
  id: string; episode_id: string | null; show_day: string; speaker: string; metric: string; symbol: string;
  comparison: string; threshold: number; horizon_date: string; claim: string; falsifier: string;
  status: string; graded_value: number | null; graded_at: string | null; read_on_air: boolean;
};
export type BullSheetRow = {
  id: number; label: string; volume: number; number: number; pressed_at: string | null;
  date: string | null; edition_time: string | null; regime: string | null; headline: string | null;
  share_url: string; app_url: string;
};
export type BullSheetPage = { total: number; offset: number; limit: number; items: BullSheetRow[] };
export type PodcastEpisodeFull = PodcastEpisode & {
  script: {
    title?: string; regime_word?: string;
    lines: { speaker: string; text: string; cites?: string[]; segment?: string }[];
    predictions?: unknown[];
    deltas?: { kind: string; name?: string; from?: string; to?: string; relation?: string; rationale?: string; source?: string }[];
  } | null;
  manifest: { id: string; unit: string; mark: string | null; label: string; url: string | null }[] | null;
  brief_text: string | null;
  fock: { hits?: unknown[]; edges?: unknown[]; error?: string | null } | null;
  transcript: string;
  predictions?: ShowPrediction[];
};
